import { useEffect, useRef, useState } from 'react';
import { useDefinirMonStatutMessagerie, useMonStatutMessagerie, type StatutMessagerie } from '../../lib/useMessagerie';

const OPTIONS: { valeur: StatutMessagerie; libelle: string; description: string; classePoint: string }[] = [
  { valeur: 'en_ligne', libelle: 'En ligne', description: 'Visible et joignable en appel', classePoint: 'bg-green-500' },
  {
    valeur: 'ne_pas_deranger',
    libelle: 'Ne pas déranger',
    description: 'Aucun appel ne pourra sonner',
    classePoint: 'bg-red-500',
  },
];

/**
 * Statut de présence choisi par l'utilisateur, façon Discord. « Absent » n'y
 * figure pas : ce n'est jamais un choix, seulement l'effet d'être déconnecté.
 */
export function SelecteurStatutMessagerie(): JSX.Element | null {
  const { data } = useMonStatutMessagerie();
  const definir = useDefinirMonStatutMessagerie();
  const [ouvert, setOuvert] = useState(false);
  const conteneurRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    function handlePointerDown(e: MouseEvent): void {
      if (conteneurRef.current && !conteneurRef.current.contains(e.target as Node)) setOuvert(false);
    }
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOuvert(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ouvert]);

  if (!data) return null;
  const actuel = OPTIONS.find((o) => o.valeur === data.statut) ?? OPTIONS[0]!;

  return (
    <div className="relative" ref={conteneurRef}>
      <button
        type="button"
        onClick={() => setOuvert((prec) => !prec)}
        className="flex min-h-[40px] items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-codialis-gray transition hover:bg-gray-100"
        aria-haspopup="menu"
        aria-expanded={ouvert}
        title="Changer mon statut"
      >
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${actuel.classePoint}`} />
        <span className="hidden md:inline">{actuel.libelle}</span>
      </button>

      {ouvert && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 w-56 rounded-xl border border-gray-100 bg-white p-1.5 text-sm shadow-lg"
        >
          {OPTIONS.map((option) => (
            <button
              key={option.valeur}
              type="button"
              role="menuitem"
              onClick={() => {
                definir.mutate(option.valeur);
                setOuvert(false);
              }}
              className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition hover:bg-gray-50 ${
                option.valeur === actuel.valeur ? 'bg-codialis-mint/10' : ''
              }`}
            >
              <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${option.classePoint}`} />
              <span>
                <div className="font-medium text-codialis-navy">{option.libelle}</div>
                <div className="text-[11px] text-codialis-gray">{option.description}</div>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
